/**
 * Edit Member Dialog Component
 *
 * Modal dialog for editing existing organization members' roles, profile, and billing info.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { ConfirmDialog } from '@components/primitives/ConfirmDialog';
import { useBillingEntities } from '@features/billing-entities';
import { useBundleCatalog, useBundlingEnabled } from '@features/subscriptions';
import { useUserId } from '@features/user';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  MenuItem,
  Select,
  Skeleton,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';
import type {
  ProviderRoleInfo,
  UpdateMemberBillingInfoRequest,
} from '@store/generated/organizations';
import { getRoleDisplayName, ROLES } from '@utils/roleUtils';
import { useSnackbar } from 'notistack';

import {
  useAddMemberBundleMutation,
  useChangeMemberBundleMutation,
  useGetMemberDetailQuery,
  useRemoveMemberBundleMutation,
  useRemoveMemberMutation,
  useUpdateMemberBillingInfoMutation,
  useUpdateMemberProfileMutation,
  useUpdateMemberRolesMutation,
} from '../store/api';
import type { OrganizationMemberTableRow } from '../types';

import { MemberBillingInfoForm } from './MemberBillingInfoForm';
import { MemberProfileForm } from './MemberProfileForm';
import { PermissionsMatrixDialog } from './PermissionsMatrixDialog';

interface EditMemberDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  member: OrganizationMemberTableRow | null;
  availableRoles?: ProviderRoleInfo[];
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`member-tabpanel-${index}`}
      aria-labelledby={`member-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `member-tab-${index}`,
    'aria-controls': `member-tabpanel-${index}`,
  };
}

export function EditMemberDialog({
  open,
  onClose,
  onSuccess,
  member,
  availableRoles = [],
}: EditMemberDialogProps) {
  const { enqueueSnackbar } = useSnackbar();
  const { clinicians } = useEventTracking();
  const [updateRoles, { isLoading: isUpdatingRoles }] = useUpdateMemberRolesMutation();
  const [updateProfile, { isLoading: isUpdatingProfile }] = useUpdateMemberProfileMutation();
  const [updateBillingInfo, { isLoading: isUpdatingBillingInfo }] = useUpdateMemberBillingInfoMutation();
  const [changeMemberBundle, { isLoading: isChangingBundle }] = useChangeMemberBundleMutation();
  const [addMemberBundle, { isLoading: isAddingBundle }] = useAddMemberBundleMutation();
  const [removeMemberBundle, { isLoading: isRemovingBundle }] = useRemoveMemberBundleMutation();
  const [removeMember, { isLoading: isDeactivating }] = useRemoveMemberMutation();
  const { isBundlingEnabled: showBundling } = useBundlingEnabled();
  const currentUserId = useUserId();
  const {
    tiers,
    getCompatibleUserAddOnsForDisplay,
    isLoading: isCatalogLoading,
  } = useBundleCatalog();

  // Fetch billing entities for billing identity selection
  const { entities: billingEntities, isLoading: entitiesLoading } = useBillingEntities();

  // Fetch member detail when dialog opens
  const { data: memberDetail, isLoading: isLoadingDetail } = useGetMemberDetailQuery(
    { userId: member?.userId || '' },
    { skip: !open || !member?.userId }
  );

  // Tab state
  const [currentTab, setCurrentTab] = useState(0);

  // Form state - Roles & Bundles
  const [selectedRoles, setSelectedRoles] = useState<Set<string>>(new Set());
  const [selectedTierId, setSelectedTierId] = useState<string>('');
  const [selectedAddOnIds, setSelectedAddOnIds] = useState<Set<string>>(new Set());
  const [confirmDeactivateOpen, setConfirmDeactivateOpen] = useState(false);
  const [permissionsDialogOpen, setPermissionsDialogOpen] = useState(false);

  // Form state - Profile
  const [profileData, setProfileData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    mobile_phone_num: '',
  });

  // Form state - Billing Info
  // Store values as strings (empty string = cleared, undefined = not set yet)
  const [billingData, setBillingData] = useState<Record<string, string | undefined>>({});

  // Initialize form state from member detail
  useEffect(() => {
    if (memberDetail) {
      // Roles & bundles
      setSelectedRoles(new Set(memberDetail.roles || []));
      const currentTier = memberDetail.bundles?.find(
        b => b.bundle_type === 'tier' && b.status === 'active',
      );
      setSelectedTierId(currentTier?.bundle_id || '');
      const currentAddOns =
        memberDetail.bundles
          ?.filter(b => b.bundle_type === 'add_on' && b.status === 'active')
          ?.map(b => b.bundle_id) || [];
      setSelectedAddOnIds(new Set(currentAddOns));

      // Profile
      setProfileData({
        first_name: memberDetail.first_name || '',
        last_name: memberDetail.last_name || '',
        email: memberDetail.email || '',
        mobile_phone_num: memberDetail.mobile_phone_num || '',
      });

      // Billing info - store as strings for form state
      const billing = memberDetail.billing_info;
      setBillingData({
        billing_entity_id: billing?.billing_entity_id || '',
        license_type: billing?.license_type || '',
        license_number: billing?.license_number || '',
        license_state: billing?.license_state || '',
        npi_number: billing?.npi_number || '',
        first_name: billing?.first_name || '',
        last_name: billing?.last_name || '',
        communications_name: billing?.communications_name || '',
        signing_name: billing?.signing_name || '',
        facility_address: billing?.facility_address || '',
        facility_address2: billing?.facility_address2 || '',
        city: billing?.city || '',
        state: billing?.state || '',
        zip: billing?.zip || '',
        phone: billing?.phone || '',
        fax_number: billing?.fax_number || '',
        tax_id: billing?.tax_id || '',
        tax_id_type: billing?.tax_id_type || '',
        place_of_service: billing?.place_of_service || '',
      });
    }
  }, [memberDetail]);

  // Reset tab when dialog opens/closes
  useEffect(() => {
    if (open) {
      setCurrentTab(0);
    }
  }, [open]);

  // Bundles are only allowed for clinicians
  const isClinician = selectedRoles.has(ROLES.CLINICIAN);
  const canSelectBundles = showBundling && isClinician;
  const isSingleTier = tiers.length === 1;

  // Detect if the member's current tier is non-purchasable
  const memberCurrentTierBundle = memberDetail?.bundles?.find(
    b => b.bundle_type === 'tier' && b.status === 'active',
  );
  const isCurrentTierNonPurchasable =
    !!memberCurrentTierBundle &&
    !tiers.some(t => t.bundle_id === memberCurrentTierBundle.bundle_id);

  // Auto-select the single tier when only one is available
  useEffect(() => {
    if (isSingleTier && tiers[0] && !selectedTierId) {
      setSelectedTierId(tiers[0].bundle_id);
    }
  }, [isSingleTier, tiers, selectedTierId]);

  // Prevent self-deactivation
  const isEditingSelf = member?.userId === currentUserId;

  // Compute available add-ons
  const availableAddOns = useMemo(
    () => getCompatibleUserAddOnsForDisplay(selectedTierId || null),
    [getCompatibleUserAddOnsForDisplay, selectedTierId],
  );

  // Get current data for comparison
  const currentTier = memberDetail?.bundles?.find(b => b.bundle_type === 'tier' && b.status === 'active');
  const currentAddOns = useMemo(
    () =>
      memberDetail?.bundles
        ?.filter(b => b.bundle_type === 'add_on' && b.status === 'active')
        .map(b => b.bundle_id) || [],
    [memberDetail?.bundles]
  );

  // Check if each section has changes
  const hasRolesChanges = useMemo(() => {
    if (!memberDetail) return false;
    const currentRolesSet = new Set(memberDetail.roles || []);
    if (selectedRoles.size !== currentRolesSet.size) return true;
    for (const role of selectedRoles) {
      if (!currentRolesSet.has(role)) return true;
    }
    return false;
  }, [memberDetail, selectedRoles]);

  const hasBundlesChanges = useMemo(() => {
    if (!memberDetail) return false;
    const currentTierId = currentTier?.bundle_id || '';
    if (selectedTierId !== currentTierId) return true;
    const currentAddOnsSet = new Set(currentAddOns);
    if (selectedAddOnIds.size !== currentAddOnsSet.size) return true;
    for (const addOnId of selectedAddOnIds) {
      if (!currentAddOnsSet.has(addOnId)) return true;
    }
    return false;
  }, [memberDetail, selectedTierId, selectedAddOnIds, currentTier, currentAddOns]);

  const hasProfileChanges = useMemo(() => {
    if (!memberDetail) return false;
    return (
      profileData.first_name !== (memberDetail.first_name || '') ||
      profileData.last_name !== (memberDetail.last_name || '') ||
      profileData.email !== (memberDetail.email || '') ||
      profileData.mobile_phone_num !== (memberDetail.mobile_phone_num || '')
    );
  }, [memberDetail, profileData]);

  const hasBillingChanges = useMemo(() => {
    if (!memberDetail?.billing_info) return false;
    const billing = memberDetail.billing_info;
    return Object.keys(billingData).some(key => {
      const currentValue = billingData[key];
      const originalValue = String(billing[key as keyof typeof billing] ?? '');
      return currentValue !== originalValue;
    });
  }, [memberDetail, billingData]);

  const hasAnyChanges = hasRolesChanges || hasBundlesChanges || hasProfileChanges || hasBillingChanges;

  // Handlers
  const handleRoleToggle = useCallback((role: string) => {
    setSelectedRoles(prev => {
      const next = new Set(prev);
      if (next.has(role)) {
        next.delete(role);
      } else {
        next.add(role);
      }
      return next;
    });
  }, []);

  const handleTierChange = useCallback(
    (newTierId: string) => {
      setSelectedTierId(newTierId);
      if (newTierId) {
        const compatibleAddOns = getCompatibleUserAddOnsForDisplay(newTierId);
        const compatibleIds = new Set(compatibleAddOns.map(a => a.bundle_id));
        setSelectedAddOnIds(prev => {
          const filtered = new Set([...prev].filter(id => compatibleIds.has(id)));
          return filtered.size !== prev.size ? filtered : prev;
        });
      } else {
        setSelectedAddOnIds(new Set());
      }
    },
    [getCompatibleUserAddOnsForDisplay],
  );

  const handleToggleAddOn = useCallback((bundleId: string) => {
    setSelectedAddOnIds(prev => {
      const next = new Set(prev);
      if (next.has(bundleId)) {
        next.delete(bundleId);
      } else {
        next.add(bundleId);
      }
      return next;
    });
  }, []);

  const handleProfileChange = useCallback((field: string, value: string) => {
    setProfileData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleBillingChange = useCallback((field: string, value: string) => {
    // For billing_entity_id, store empty string to distinguish from undefined (not changed)
    // We'll convert to null when building the payload
    setBillingData(prev => ({ ...prev, [field]: value || '' }));
  }, []);

  const handleSave = useCallback(async () => {
    if (!member || !memberDetail) return;

    try {
      // Save roles if changed
      if (hasRolesChanges) {
        const currentRolesSet = new Set(memberDetail.roles || []);
        await updateRoles({
          userId: member.userId,
          providerUpdateRolesRequest: {
            roles: Array.from(selectedRoles),
          },
        }).unwrap();
        const rolesAdded = Array.from(selectedRoles).filter(r => !currentRolesSet.has(r)).length;
        const rolesRemoved = Array.from(currentRolesSet).filter((r: string) => !selectedRoles.has(r)).length;
        clinicians.memberRoleChanged({
          member_id: member.userId,
          role_count: selectedRoles.size,
          roles_added: rolesAdded,
          roles_removed: rolesRemoved,
        });
      }

      // Handle bundle changes if bundling is enabled and user is a clinician
      let bundleChanged = false;
      let tierChanged = false;
      let addonsAdded = 0;
      let addonsRemoved = 0;
      if (canSelectBundles) {
        const currentTierId = currentTier?.bundle_id || '';
        const currentAddOnsSet = new Set(currentAddOns);

        // Handle tier change
        if (selectedTierId !== currentTierId) {
          tierChanged = true;
          bundleChanged = true;
          if (currentTierId && selectedTierId && currentTier) {
            await changeMemberBundle({
              userId: member.userId,
              assignmentId: currentTier.assignment_id,
              adminAddBundleRequest: {
                bundle_id: selectedTierId,
              },
            }).unwrap();
          } else if (selectedTierId) {
            await addMemberBundle({
              userId: member.userId,
              adminAddBundleRequest: {
                bundle_id: selectedTierId,
              },
            }).unwrap();
          }
        }

        // Handle add-on changes
        const addOnsToAdd = Array.from(selectedAddOnIds).filter(id => !currentAddOnsSet.has(id));
        const addOnsToRemove = Array.from(currentAddOnsSet).filter(id => !selectedAddOnIds.has(id));
        addonsAdded = addOnsToAdd.length;
        addonsRemoved = addOnsToRemove.length;

        if (addonsAdded > 0 || addonsRemoved > 0) {
          bundleChanged = true;
        }

        for (const addOnId of addOnsToAdd) {
          await addMemberBundle({
            userId: member.userId,
            adminAddBundleRequest: {
              bundle_id: addOnId,
            },
          }).unwrap();
        }

        for (const addOnId of addOnsToRemove) {
          const addOnBundle = memberDetail.bundles?.find(
            b => b.bundle_id === addOnId && b.bundle_type === 'add_on' && b.status === 'active',
          );
          if (addOnBundle) {
            await removeMemberBundle({
              userId: member.userId,
              assignmentId: addOnBundle.assignment_id,
            }).unwrap();
          }
        }

        // Track bundle changes
        if (bundleChanged) {
          clinicians.memberBundleChanged({
            member_id: member.userId,
            tier_changed: tierChanged,
            from_tier_id: currentTierId || undefined,
            to_tier_id: selectedTierId || undefined,
            addons_added: addonsAdded,
            addons_removed: addonsRemoved,
            addon_ids_added: addOnsToAdd,
            addon_ids_removed: addOnsToRemove,
            final_addon_count: selectedAddOnIds.size,
          });
        }
      }

      // Save profile if changed
      if (hasProfileChanges) {
        await updateProfile({
          userId: member.userId,
          updateMemberProfileRequest: {
            first_name: profileData.first_name,
            last_name: profileData.last_name,
            email: profileData.email,
            mobile_phone_num: profileData.mobile_phone_num,
          },
        }).unwrap();
      }

      // Save billing info if changed
      if (hasBillingChanges) {
        // Build payload with only changed fields
        const billing = memberDetail.billing_info;
        const payload: UpdateMemberBillingInfoRequest = {};
        
        // Helper to add field to payload if changed
        const addIfChanged = (
          field: keyof UpdateMemberBillingInfoRequest,
          currentValue: string | undefined
        ) => {
          const originalValue = String(billing?.[field] ?? '');
          if (currentValue !== originalValue) {
            // Empty string means clear the field (set to null)
            // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
            (payload as any)[field] = currentValue === '' ? null : currentValue;
          }
        };

        // Check each field
        addIfChanged('billing_entity_id', billingData.billing_entity_id);
        addIfChanged('license_type', billingData.license_type);
        addIfChanged('license_number', billingData.license_number);
        addIfChanged('license_state', billingData.license_state);
        addIfChanged('npi_number', billingData.npi_number);
        addIfChanged('first_name', billingData.first_name);
        addIfChanged('last_name', billingData.last_name);
        addIfChanged('communications_name', billingData.communications_name);
        addIfChanged('signing_name', billingData.signing_name);
        addIfChanged('facility_address', billingData.facility_address);
        addIfChanged('facility_address2', billingData.facility_address2);
        addIfChanged('city', billingData.city);
        addIfChanged('state', billingData.state);
        addIfChanged('zip', billingData.zip);
        addIfChanged('phone', billingData.phone);
        addIfChanged('fax_number', billingData.fax_number);
        addIfChanged('tax_id', billingData.tax_id);
        addIfChanged('tax_id_type', billingData.tax_id_type);
        addIfChanged('place_of_service', billingData.place_of_service);

        await updateBillingInfo({
          userId: member.userId,
          updateMemberBillingInfoRequest: payload,
        }).unwrap();
      }

      enqueueSnackbar('Member updated successfully', { variant: 'success' });
      onSuccess();
    } catch (error) {
      const message =
        error && typeof error === 'object' && 'data' in error && error.data && typeof error.data === 'object' && 'message' in error.data
          ? String(error.data.message)
          : 'Failed to update member';
      enqueueSnackbar(message, { variant: 'error' });
    }
  }, [
    member,
    memberDetail,
    hasRolesChanges,
    hasBundlesChanges,
    hasProfileChanges,
    hasBillingChanges,
    selectedRoles,
    selectedTierId,
    selectedAddOnIds,
    profileData,
    billingData,
    canSelectBundles,
    currentTier,
    currentAddOns,
    updateRoles,
    updateProfile,
    updateBillingInfo,
    changeMemberBundle,
    addMemberBundle,
    removeMemberBundle,
    enqueueSnackbar,
    onSuccess,
    clinicians,
  ]);

  const isLoading =
    isLoadingDetail ||
    isUpdatingRoles ||
    isUpdatingProfile ||
    isUpdatingBillingInfo ||
    isChangingBundle ||
    isAddingBundle ||
    isRemovingBundle ||
    isCatalogLoading ||
    isDeactivating;

  const handleClose = useCallback(() => {
    if (!isLoading) {
      onClose();
    }
  }, [isLoading, onClose]);

  const handleDeactivate = useCallback(async () => {
    if (!member) return;

    try {
      await removeMember({ userId: member.userId }).unwrap();
      clinicians.memberDeactivated({ member_id: member.userId });
      enqueueSnackbar('Member deactivated successfully', { variant: 'success' });
      setConfirmDeactivateOpen(false);
      onSuccess();
    } catch (error) {
      const message =
        error && typeof error === 'object' && 'data' in error && error.data && typeof error.data === 'object' && 'message' in error.data
          ? String(error.data.message)
          : 'Failed to deactivate member';
      enqueueSnackbar(message, { variant: 'error' });
    }
  }, [member, removeMember, enqueueSnackbar, onSuccess, clinicians]);

  // Get available roles
  const rolesToDisplay =
    availableRoles.length > 0
      ? availableRoles.filter(r => !r.role.includes('billing'))
      : [
          {
            role: ROLES.CLINICIAN,
            display_name: 'Clinician',
            description: 'Clinical staff member',
          },
          {
            role: ROLES.OPERATIONS_SUPPORT,
            display_name: 'Operations Support',
            description: 'Operations team member',
          },
          {
            role: ROLES.PRACTICE_ADMINISTRATOR,
            display_name: 'Practice Administrator',
            description: 'Practice admin with billing access',
          },
          {
            role: ROLES.SUPERVISOR,
            display_name: 'Supervisor',
            description: 'Clinical supervisor',
          },
        ];

  if (!member) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Edit Member
        <IconButton
          aria-label="close"
          onClick={handleClose}
          disabled={isLoading}
          size="small"
          sx={{ color: 'text.secondary' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Member Info */}
          <Box>
            <Typography variant="body1" fontWeight={500}>
              {member.name || 'Unknown'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {member.email || 'No email'}
            </Typography>
          </Box>

          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={currentTab}
              onChange={(_, newValue) => setCurrentTab(newValue as number)}
              aria-label="Member edit sections"
            >
              <Tab label="Roles & Subscription" {...a11yProps(0)} />
              <Tab label="Profile" {...a11yProps(1)} />
              {isClinician && <Tab label="Billing Information" {...a11yProps(2)} />}
            </Tabs>
          </Box>

          {/* Tab Panels */}
          <TabPanel value={currentTab} index={0}>
            {/* Role Selection */}
            <FormControl component="fieldset">
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Roles
                </Typography>
                <Button
                  size="small"
                  onClick={() => setPermissionsDialogOpen(true)}
                  startIcon={<InfoOutlinedIcon sx={{ fontSize: '1rem' }} />}
                  sx={{ textTransform: 'none', fontSize: '0.75rem', p: 0, minWidth: 'auto' }}
                >
                  View role permissions
                </Button>
              </Box>
              <FormGroup>
                {rolesToDisplay.map(role => (
                  <FormControlLabel
                    key={role.role}
                    control={
                      <Checkbox
                        checked={selectedRoles.has(role.role)}
                        onChange={() => handleRoleToggle(role.role)}
                        disabled={isLoading}
                        size="small"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span>{role.display_name || getRoleDisplayName(role.role)}</span>
                        {role.description && (
                          <Typography variant="caption" color="text.secondary">
                            {role.description}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                ))}
              </FormGroup>
            </FormControl>

            {/* Bundle Selection */}
            {canSelectBundles && (
              <>
                <Divider sx={{ my: 3 }} />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                    Subscription Plan
                  </Typography>

                  {isCatalogLoading ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Skeleton variant="rounded" height={40} />
                      <Box sx={{ minHeight: 135 }}>
                        <Skeleton variant="text" width={60} />
                        <Skeleton variant="rectangular" height={32} sx={{ mt: 1 }} />
                        <Skeleton variant="rectangular" height={32} sx={{ mt: 1 }} />
                      </Box>
                    </Box>
                  ) : (
                    <>
                      {isSingleTier && !isCurrentTierNonPurchasable ? (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, mb: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                          <Typography variant="body2" fontWeight={500}>{tiers[0].name}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            ${(tiers[0].monthly_unit_amount / 100).toFixed(2)}/mo
                          </Typography>
                        </Box>
                      ) : (
                        <FormControl fullWidth variant="outlined" size="small" sx={{ mb: 2 }}>
                          <Select
                            value={selectedTierId}
                            onChange={e => handleTierChange(e.target.value)}
                            disabled={isLoading}
                            displayEmpty
                          >
                            {isCurrentTierNonPurchasable && memberCurrentTierBundle && (
                              <MenuItem value={memberCurrentTierBundle.bundle_id} disabled>
                                {memberCurrentTierBundle.bundle_name} (Current plan)
                              </MenuItem>
                            )}
                            {tiers.map(tier => (
                              <MenuItem key={tier.bundle_id} value={tier.bundle_id}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                  <span>{tier.name}</span>
                                  <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                                    ${(tier.monthly_unit_amount / 100).toFixed(2)}/mo
                                  </Typography>
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}

                      {selectedTierId && availableAddOns.length > 0 && (
                        <FormControl component="fieldset" variant="standard">
                          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                            Add-ons
                          </Typography>
                          <FormGroup>
                            {availableAddOns.map(addOn => (
                              <FormControlLabel
                                key={addOn.bundle_id}
                                control={
                                  <Checkbox
                                    checked={selectedAddOnIds.has(addOn.bundle_id)}
                                    onChange={() => handleToggleAddOn(addOn.bundle_id)}
                                    disabled={isLoading}
                                    size="small"
                                  />
                                }
                                label={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <span>{addOn.name}</span>
                                    <Typography variant="caption" color="text.secondary">
                                      ${(addOn.monthly_unit_amount / 100).toFixed(2)}/mo
                                    </Typography>
                                  </Box>
                                }
                              />
                            ))}
                          </FormGroup>
                        </FormControl>
                      )}
                    </>
                  )}
                </Box>
              </>
            )}
          </TabPanel>

          <TabPanel value={currentTab} index={1}>
            <MemberProfileForm
              firstName={profileData.first_name}
              lastName={profileData.last_name}
              email={profileData.email}
              phone={profileData.mobile_phone_num}
              onChange={handleProfileChange}
              disabled={isLoading}
            />
          </TabPanel>

          {isClinician && (
            <TabPanel value={currentTab} index={2}>
              <MemberBillingInfoForm
                data={billingData}
                onChange={handleBillingChange}
                disabled={isLoading}
                billingEntities={billingEntities}
                entitiesLoading={entitiesLoading}
              />
            </TabPanel>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 3 }}>
        {!isEditingSelf && (
          <Button
            variant="outlined"
            color="error"
            onClick={() => setConfirmDeactivateOpen(true)}
            disabled={isLoading}
          >
            Deactivate Member
          </Button>
        )}
        <Box sx={{ display: 'flex', gap: 1, ml: isEditingSelf ? 'auto' : 0 }}>
          <Button onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            color="primary"
            variant="contained"
            disabled={
              isLoading ||
              !hasAnyChanges ||
              (canSelectBundles && selectedRoles.has(ROLES.CLINICIAN) && !selectedTierId)
            }
            startIcon={isLoading ? <CircularProgress size={16} /> : null}
          >
            {isLoading ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </DialogActions>

      {/* Deactivation Confirmation Dialog */}
      <ConfirmDialog
        open={confirmDeactivateOpen}
        variant="error"
        title="Deactivate Member?"
        message={`Are you sure you want to deactivate ${member?.name || 'this member'}? They will lose access to the organization and their subscription will be cancelled.`}
        onClose={() => setConfirmDeactivateOpen(false)}
        onConfirm={handleDeactivate}
        cancelText="Cancel"
        confirmText="Deactivate"
        loading={isDeactivating}
      />

      {/* Permissions Matrix Dialog */}
      <PermissionsMatrixDialog
        open={permissionsDialogOpen}
        onClose={() => setPermissionsDialogOpen(false)}
      />
    </Dialog>
  );
}
